import { Box, Button, Dropdown, Flex, Input, Text, useToast } from '@lego/klik-ui';
import { CronExpressionParser } from 'cron-parser';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ToastOptions } from '@/utility/ToastOptions';

// This is heavily inspired by https://github.com/xrutayisire/react-js-cron/blob/master/src/Cron.tsx. For future updates, please look at this implementation.
export type CronPeriod = 'minute' | 'hour' | 'day' | 'week' | 'month';

type CronBuilderProps = {
  value?: string;
  onChange: (cronExpression: string | undefined) => void;
};

const MINUTES = Array.from({ length: 60 }, (_, i) => i);
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const DAYS_OF_MONTH = Array.from({ length: 31 }, (_, i) => i + 1);
export const WEEKDAYS = [
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
  { value: '0', label: 'Sunday' },
];
export const MONTHS = [
  { value: '*', label: 'Every month' },
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

export type ParsedCron = {
  period: CronPeriod;
  minute: string;
  hour: string;
  dayOfMonth: string;
  month: string;
  dayOfWeek: string;
};

export const determinePeriod = (
  minute: string,
  hour: string,
  dayOfMonth: string,
  dayOfWeek: string
): CronPeriod => {
  if (minute !== '*' && hour === '*') return 'hour';
  if (minute !== '*' && hour !== '*' && dayOfMonth === '*' && dayOfWeek === '*') return 'day';
  if (minute !== '*' && hour !== '*' && dayOfMonth === '*' && dayOfWeek !== '*') return 'week';
  if (minute !== '*' && hour !== '*' && dayOfMonth !== '*') return 'month';
  return 'minute';
};

export const buildCronFromParts = (
  period: CronPeriod,
  minute: string,
  hour: string,
  dayOfMonth: string,
  dayOfWeek: string,
  month: string
): string => {
  const cronMap: Record<CronPeriod, string> = {
    minute: '* * * * *',
    hour: `${minute} * * * *`,
    day: `${minute} ${hour} * * *`,
    week: `${minute} ${hour} * * ${dayOfWeek}`,
    month: `${minute} ${hour} ${dayOfMonth} ${month} *`,
  };
  return cronMap[period];
};

const isSimpleCronField = (field: string) => field === '*' || /^\d+$/.test(field);

export const parseCronExpression = (cron: string): ParsedCron | null => {
  try {
    if (cron.trim().split(/\s+/).length !== 5) return null;

    const expression = CronExpressionParser.parse(cron).stringify();

    const [minute, hour, dayOfMonth, month, dayOfWeek] = expression.split(' ');
    if (![minute, hour, dayOfMonth, month, dayOfWeek].every(isSimpleCronField)) return null;

    const period = determinePeriod(minute, hour, dayOfMonth, dayOfWeek);
    const parsed = { period, minute, hour, dayOfMonth, month, dayOfWeek };
    return buildCronFromParts(period, minute, hour, dayOfMonth, dayOfWeek, month) === expression
      ? parsed
      : null;
  } catch {
    return null;
  }
};

const formatTime = (hour: string, minute: string): string => {
  return `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
};

const getDaySuffix = (day: string): string => {
  if (day === '1') return 'st';
  if (day === '2') return 'nd';
  if (day === '3') return 'rd';
  return 'th';
};

export const getHumanReadableDescription = (
  period: CronPeriod,
  minute: string,
  hour: string,
  dayOfMonth: string,
  dayOfWeek: string,
  month: string
): string => {
  const time = formatTime(hour, minute);

  switch (period) {
    case 'minute':
      return 'Every minute';
    case 'hour':
      return `Every hour at minute ${minute}`;
    case 'day':
      return `Every day at ${time}`;
    case 'week': {
      const dayName = WEEKDAYS.find((d) => d.value === dayOfWeek)?.label || dayOfWeek;
      return `Every ${dayName} at ${time}`;
    }
    case 'month': {
      const monthName =
        month === '*' ? 'month' : MONTHS.find((m) => m.value === month)?.label || month;
      return `Every ${monthName} on the ${dayOfMonth}${getDaySuffix(dayOfMonth)} at ${time}`;
    }
  }
};

type SimpleModeProps = {
  period: CronPeriod;
  minute: string;
  hour: string;
  dayOfMonth: string;
  dayOfWeek: string;
  month: string;
  onPeriodChange: (period: string) => void;
  onMinuteChange: (minute: string) => void;
  onHourChange: (hour: string) => void;
  onDayOfMonthChange: (day: string) => void;
  onDayOfWeekChange: (day: string) => void;
  onMonthChange: (month: string) => void;
};

const SimpleMode = ({
  period,
  minute,
  hour,
  dayOfMonth,
  dayOfWeek,
  month,
  onPeriodChange,
  onMinuteChange,
  onHourChange,
  onDayOfMonthChange,
  onDayOfWeekChange,
  onMonthChange,
}: SimpleModeProps) => (
  <Flex direction="column" gap={3}>
    <Flex alignItems="center" gap={2}>
      <Text minWidth="60px">Every</Text>
      <Dropdown defaultValue={[period]} flex={1} onChange={onPeriodChange}>
        <Dropdown.Option value="minute">Minute</Dropdown.Option>
        <Dropdown.Option value="hour">Hour</Dropdown.Option>
        <Dropdown.Option value="day">Day</Dropdown.Option>
        <Dropdown.Option value="week">Week</Dropdown.Option>
        <Dropdown.Option value="month">Month</Dropdown.Option>
      </Dropdown>
    </Flex>

    {period === 'week' && (
      <Flex alignItems="center" gap={2}>
        <Text minWidth="60px">On</Text>
        <Dropdown defaultValue={[dayOfWeek]} flex={1} onChange={onDayOfWeekChange}>
          {WEEKDAYS.map((day) => (
            <Dropdown.Option key={day.value} value={day.value}>
              {day.label}
            </Dropdown.Option>
          ))}
        </Dropdown>
      </Flex>
    )}

    {period === 'month' && (
      <>
        <Flex alignItems="center" gap={2}>
          <Text minWidth="60px">In</Text>
          <Dropdown defaultValue={[month]} flex={1} onChange={onMonthChange}>
            {MONTHS.map((m) => (
              <Dropdown.Option key={m.value} value={m.value}>
                {m.label}
              </Dropdown.Option>
            ))}
          </Dropdown>
        </Flex>
        <Flex alignItems="center" gap={2}>
          <Text minWidth="60px">On day</Text>
          <Dropdown defaultValue={[dayOfMonth]} flex={1} onChange={onDayOfMonthChange}>
            {DAYS_OF_MONTH.map((d) => (
              <Dropdown.Option key={d} value={d.toString()}>
                {d}
              </Dropdown.Option>
            ))}
          </Dropdown>
        </Flex>
      </>
    )}

    {period !== 'minute' && (
      <Flex alignItems="center" gap={2}>
        <Text minWidth="60px">At</Text>
        {period !== 'hour' && (
          <Dropdown defaultValue={[hour]} flex={1} onChange={onHourChange}>
            {HOURS.map((h) => (
              <Dropdown.Option key={h} value={h.toString()}>
                {h.toString().padStart(2, '0')}
              </Dropdown.Option>
            ))}
          </Dropdown>
        )}
        {period !== 'hour' && <Text>:</Text>}
        <Dropdown defaultValue={[minute]} flex={1} onChange={onMinuteChange}>
          {MINUTES.map((m) => (
            <Dropdown.Option key={m} value={m.toString()}>
              {m.toString().padStart(2, '0')}
            </Dropdown.Option>
          ))}
        </Dropdown>
        {period === 'hour' && <Text>minutes past the hour</Text>}
      </Flex>
    )}
  </Flex>
);

const DEFAULT_CRON_STATE = {
  period: 'day' as CronPeriod,
  minute: '0',
  hour: '0',
  dayOfMonth: '1',
  dayOfWeek: '1',
  month: '*',
};

export const CronBuilder = ({ value, onChange }: CronBuilderProps) => {
  const [isAdvancedMode, setIsAdvancedMode] = useState(false);
  const [advancedValue, setAdvancedValue] = useState(value || '');
  const toast = useToast();

  const parsed = useMemo(() => parseCronExpression(value || ''), [value]);

  const [period, setPeriod] = useState<CronPeriod>(parsed?.period || DEFAULT_CRON_STATE.period);
  const [minute, setMinute] = useState(parsed?.minute || DEFAULT_CRON_STATE.minute);
  const [hour, setHour] = useState(parsed?.hour || DEFAULT_CRON_STATE.hour);
  const [dayOfMonth, setDayOfMonth] = useState(parsed?.dayOfMonth || DEFAULT_CRON_STATE.dayOfMonth);
  const [dayOfWeek, setDayOfWeek] = useState(parsed?.dayOfWeek || DEFAULT_CRON_STATE.dayOfWeek);
  const [month, setMonth] = useState(parsed?.month || DEFAULT_CRON_STATE.month);

  // Reset simple mode state when external value is cleared (e.g., form reset)
  useEffect(() => {
    if (value === undefined || value === '') {
      setAdvancedValue('');
      setPeriod(DEFAULT_CRON_STATE.period);
      setMinute(DEFAULT_CRON_STATE.minute);
      setHour(DEFAULT_CRON_STATE.hour);
      setDayOfMonth(DEFAULT_CRON_STATE.dayOfMonth);
      setDayOfWeek(DEFAULT_CRON_STATE.dayOfWeek);
      setMonth(DEFAULT_CRON_STATE.month);
    }
  }, [value]);

  const cronExpression = useMemo(
    () => buildCronFromParts(period, minute, hour, dayOfMonth, dayOfWeek, month),
    [period, minute, hour, dayOfMonth, dayOfWeek, month]
  );

  // Always emit the current simple-mode cron expression when in simple mode
  useEffect(() => {
    if (!isAdvancedMode) {
      onChange(cronExpression);
    }
  }, [cronExpression, isAdvancedMode, onChange]);

  // Emit advanced value when it changes (but not on mode switch - that's handled separately)
  const handleAdvancedValueChange = useCallback(
    (newValue: string) => {
      setAdvancedValue(newValue);
      onChange(newValue || undefined);
    },
    [onChange]
  );

  // Handle switching to advanced mode - always pre-populate from current simple expression
  const switchToAdvancedMode = useCallback(() => {
    setIsAdvancedMode(true);
    setAdvancedValue(cronExpression);
    onChange(cronExpression || undefined);
  }, [cronExpression, onChange]);

  const switchToSimpleMode = useCallback(() => {
    const parsedAdvanced = parseCronExpression(advancedValue);
    if (!parsedAdvanced) {
      toast(
        ToastOptions.error(
          'Cannot switch to Simple mode',
          'Use individual values and wildcards. Ranges, lists, and steps require Advanced mode.'
        )
      );
      return;
    }

    setPeriod(parsedAdvanced.period);
    setMinute(parsedAdvanced.minute);
    setHour(parsedAdvanced.hour);
    setDayOfMonth(parsedAdvanced.dayOfMonth);
    setDayOfWeek(parsedAdvanced.dayOfWeek);
    setMonth(parsedAdvanced.month);
    setIsAdvancedMode(false);
  }, [advancedValue, toast]);

  const handlePeriodChange = useCallback((newPeriod: string) => {
    setPeriod(newPeriod as CronPeriod);
  }, []);

  const handleMinuteChange = useCallback((newMinute: string) => {
    setMinute(newMinute);
  }, []);

  const handleHourChange = useCallback((newHour: string) => {
    setHour(newHour);
  }, []);

  const handleDayOfMonthChange = useCallback((newDay: string) => {
    setDayOfMonth(newDay);
  }, []);

  const handleDayOfWeekChange = useCallback((newDay: string) => {
    setDayOfWeek(newDay);
  }, []);

  const handleMonthChange = useCallback((newMonth: string) => {
    setMonth(newMonth);
  }, []);

  const description = useMemo(() => {
    if (isAdvancedMode) {
      return advancedValue ? `Cron: ${advancedValue}` : 'Enter a cron expression';
    }
    return getHumanReadableDescription(period, minute, hour, dayOfMonth, dayOfWeek, month);
  }, [isAdvancedMode, advancedValue, period, minute, hour, dayOfMonth, dayOfWeek, month]);

  return (
    <Box>
      <Flex alignItems="center" gap={2} marginBottom={3}>
        <Button
          color={isAdvancedMode ? 'gray.500' : 'light-blue.600'}
          fontWeight={isAdvancedMode ? 'normal' : 'bold'}
          onClick={switchToSimpleMode}
          size="sm"
          textDecoration={isAdvancedMode ? 'none' : 'underline'}
          variant="link"
        >
          Simple
        </Button>
        <Text color="gray.400">|</Text>
        <Button
          color={isAdvancedMode ? 'light-blue.600' : 'gray.500'}
          data-testid="cron-builder-advanced-mode"
          fontWeight={isAdvancedMode ? 'bold' : 'normal'}
          onClick={switchToAdvancedMode}
          size="sm"
          textDecoration={isAdvancedMode ? 'underline' : 'none'}
          variant="link"
        >
          Advanced
        </Button>
      </Flex>

      {isAdvancedMode ? (
        <Input
          data-testid="cron-expression-input"
          onChange={({ target: { value: v } }) => handleAdvancedValueChange(v)}
          placeholder="e.g. 0 18 * * 1-5"
          value={advancedValue}
        />
      ) : (
        <SimpleMode
          dayOfMonth={dayOfMonth}
          dayOfWeek={dayOfWeek}
          hour={hour}
          minute={minute}
          month={month}
          onDayOfMonthChange={handleDayOfMonthChange}
          onDayOfWeekChange={handleDayOfWeekChange}
          onHourChange={handleHourChange}
          onMinuteChange={handleMinuteChange}
          onMonthChange={handleMonthChange}
          onPeriodChange={handlePeriodChange}
          period={period}
        />
      )}

      <Text color="gray.600" fontSize="sm" fontStyle="italic" marginTop={2}>
        {description}
      </Text>
    </Box>
  );
};
